import { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../../../../../context/AuthContext";
import { AdminOrderService } from "../../../../../services/api/AdminOrderService/admin.order.service";
import { OfflineItemService } from "../../../../../services/api/OfflineOrder/offline.service";
import { OrderService } from "../../../../../services/api/order.service";
import { Product, SelectedProduct, StockItem } from "../OfflineOrder/types";
import medicineIcon from "../../../../../assets/images/medicineIcon.png";
import OfflineBill from "../OfflineOrder/OfflineBill";

import PatientDetailsCard from "./CreateOrderComponents/PatientDetailsCard";
import OrderTable from "./CreateOrderComponents/OrderTable";
import OrderSidebar from "./CreateOrderComponents/OrderSidebar";
import AddMedicineModal from "./CreateOrderComponents/AddMedicineModal";
import { ChevronLeft, Loader2 } from "lucide-react";

interface CreateOrderProps {
  handleBack: () => void;
  onOrderSuccess?: () => void;
}

interface PatientFormData {
  patientName: string;
  patientMobile: string;
  patientAddress: string;
  patientPincode: string;
  paymentType: string;
  drName: string;
  mobileNumber: string;
  registrationNumber: string;
  clinicName: string;
  dlno: string;
  address: string;
  pincode: string;
  cashAmount: number;
}

const CreateOrder: React.FC<CreateOrderProps> = ({ handleBack, onOrderSuccess: _onOrderSuccess }) => {
  const { user } = useAuth();
  const clinicId = user?.role === "clinic" ? user?.id : "";

  // Patient form state
  const [formData, setFormData] = useState<PatientFormData>({
    patientName: "",
    patientMobile: "",
    patientAddress: "",
    patientPincode: "",
    paymentType: "Cash",
    drName: "",
    mobileNumber: "",
    registrationNumber: "",
    clinicName: "",
    dlno: "",
    address: "",
    pincode: "",
    cashAmount: 0,
  });

  // Products state
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showBill, setShowBill] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extraDiscount, setExtraDiscount] = useState(0);
  const [showDiscountInput, setShowDiscountInput] = useState(false);
  const [discountValue, setDiscountValue] = useState("");
  const [existingCoins, setExistingCoins] = useState(0);
  const [coinsused, setCoinsused] = useState<number | "">(0);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalItemsCount: 0,
    totalPages: 1,
    nextPage: null as number | null,
    previousPage: null as number | null,
  });

  // Fetch clinic details on mount
  useEffect(() => {
    const fetchClinicDetails = async () => {
      try {
        if (!clinicId) return;
        const response = await OfflineItemService.getClinicDetailsById(clinicId);
        const clinicData = response.data[0] || {};
        setFormData((prev) => ({ ...prev, ...clinicData }));
      } catch (err) {
        console.error("Failed to load clinic details:", err);
      }
    };
    fetchClinicDetails();
  }, [clinicId]);

  // Fetch products for the modal
  const fetchProducts = useCallback(
    async (searchTerm = "", page = 1, itemsPerPage = 10) => {
      try {
        setLoading(true);
        const response = await AdminOrderService.getClinicProductListing(
          page,
          itemsPerPage,
          searchTerm,
          clinicId
        );
        if (response?.data?.data && Array.isArray(response.data.data)) {
          const transformedProducts = response.data.data.map((item: StockItem) => ({
            compositeId: `${item.id}_${item.product_id}`,
            id: item.id,
            product_id: item.product_id,
            product_name: item.product?.name || "Unknown Product",
            manufacturer_name: item.manufacturer?.manufacturer_name || "Unknown Manufacturer",
            product_image: item.product?.productImage || medicineIcon,
            landing_cost: parseFloat(item.landing_cost) || 0,
            mrp: parseFloat(item.productMrp || item.mrp),
            quantity: item.no_of_items || item.remaining_stocks || 0,
            expiry_date: item.expiry_date,
            batch_number: item.batch_number,
            hsn_number: item.hsn_number,
            product_rate: parseFloat(item.product_rate) || 0,
            discount: parseFloat(item.sellingDiscount) || 0,
            cgst: parseFloat(item.cgst) || 0,
            sgst: parseFloat(item.sgst) || 0,
            free_items: item.free_items || 0,
            finalSP:
              parseFloat(item.finalSP) /
              (item?.product?.tablet_per_stripe
                ? Math.max(1, item.product.tablet_per_stripe)
                : 1),
            sellingDiscount: 0,
            remainingStocks: item.remaining_stocks,
            totalStock: item.totalQuantity
              ? item.totalQuantity
              : item.remaining_stocks *
                (item.product.tablet_per_stripe ? item.product.tablet_per_stripe : 1),
            noOfItemsInPack: item?.product?.tablet_per_stripe
              ? Math.max(1, item.product.tablet_per_stripe)
              : 1,
            perPackPrice:
              parseFloat(item.productMrp || item.mrp) /
                (item?.product?.tablet_per_stripe
                  ? Math.max(1, item.product.tablet_per_stripe)
                  : 1) || 0,
            expiry: item.expiry_date,
            batch: item.batch_number,
            salt: item.salt,
          }));
          setAvailableProducts(transformedProducts);
          setPagination(
            response.data.pagination || {
              currentPage: 1,
              itemsPerPage: 10,
              totalItemsCount: 0,
              totalPages: 1,
              nextPage: null,
              previousPage: null,
            }
          );
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to load products");
      } finally {
        setLoading(false);
      }
    },
    [clinicId]
  );

  // Debounced products fetch for the modal
  useEffect(() => {
    if (!showModal) return;

    const timer = setTimeout(() => {
      fetchProducts(searchText, 1, pagination.itemsPerPage);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText, showModal, fetchProducts, pagination.itemsPerPage]);

  // Patient lookup by mobile
  const fetchPatientDetails = async (mobileNumber: string) => {
    try {
      if (mobileNumber.length === 10 && /^\d{10}$/.test(mobileNumber)) {
        const response = await OfflineItemService.getPatientByNumber(mobileNumber);
        if (response.success && response.data) {
          const patientData = response.data;
          setFormData((prev) => ({
            ...prev,
            patientName: patientData.patient_name,
            patientAddress: patientData.patient_address,
            patientPincode: patientData.patient_pincode,
          }));
          setExistingCoins(patientData?.coins || 0);
        }
      }
    } catch (error) {
      console.error("Failed to fetch patient details:", error);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === "patientMobile") {
      if (!/^\d*$/.test(value)) return;
      if (value.length > 10) return;
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (value.length === 10) fetchPatientDetails(value);
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleModalSearch = (value: string) => {
    setSearchText(value);
  };

  const handleAddProduct = (product: Product) => {
    const existingProduct = selectedProducts.find(
      (p) => p.compositeId === product.compositeId
    );
    
    if (existingProduct) {
      // Logic for adding more is now handled by quantity updates in table
      // In modal, clicking an added product will call handleRemoveProduct instead
      return;
    }

    if (product.totalStock <= 0) {
      toast.error(`Out of stock!`);
      return;
    }

    setSelectedProducts((prev) => [...prev, { ...product, selectedQuantity: 0 }]);
    setSearchText(""); // Clear search bar once selected

    toast.success(`Added "${product.product_name}"`, {
      icon: "✅",
      style: {
        background: "#199C4D",
        color: "#fff",
        borderRadius: "8px",
        padding: "12px 20px",
        fontWeight: 500,
      },
      duration: 2000,
    });
  };

  // Convert string inputs back to numbers and update total quantity
  const handleQuantityInput = (compositeId: string, stripsStr: string, tabletsStr: string, noOfItemsInPack: number) => {
    const strips = stripsStr === "" ? 0 : parseInt(stripsStr, 10);
    const tablets = tabletsStr === "" ? 0 : parseInt(tabletsStr, 10);
    
    // Prevent NaN
    if (isNaN(strips) || isNaN(tablets)) return;

    const newQuantity = (strips * noOfItemsInPack) + tablets;

    const product = availableProducts.find((p) => p.compositeId === compositeId)
      || selectedProducts.find((p) => p.compositeId === compositeId);
      
    if (!product) return;
    
    if (newQuantity > product.totalStock) {
      toast.error(`Stock unavailable! Only ${product.totalStock} items left`);
      return;
    }

    if (newQuantity <= 0 && stripsStr !== "" && tabletsStr !== "") {
        // If user explicitly typed 0 for both, we keep it at 0. We'll only remove if explicit action or if they hit 0 and leave it
        setSelectedProducts((prev) =>
            prev.map((p) => (p.compositeId === compositeId ? { ...p, selectedQuantity: 0 } : p))
        );
        return;
    }

    setSelectedProducts((prev) =>
      prev.map((p) => (p.compositeId === compositeId ? { ...p, selectedQuantity: newQuantity } : p))
    );
  };

  const handleRemoveProduct = (compositeId: string) => {
    setSelectedProducts((prev) => prev.filter((p) => p.compositeId !== compositeId));
  };

  const onUpdateDiscount = (productId: string, discount: number) => {
    const product = availableProducts.find((p) => p.compositeId === productId)
      || selectedProducts.find((p) => p.compositeId === productId);
    const select = selectedProducts.find((p) => p.compositeId === productId);

    if (isNaN(discount)) discount = 0;
    if (!product || !select) return;

    if (discount > product.discount) {
      toast.error("Price less than selling price");
      setSelectedProducts((prev) =>
        prev.map((p) => (p.compositeId === productId ? { ...p, sellingDiscount: 0 } : p))
      );
      return;
    }

    setSelectedProducts((prev) =>
      prev.map((p) => (p.compositeId === productId ? { ...p, sellingDiscount: discount } : p))
    );
  };

  const calculateTotals = () => {
    const totalItems = selectedProducts.reduce((sum, p) => sum + p.selectedQuantity, 0);
    const totalAmount = selectedProducts.reduce((sum, product) => {
      const mrp = product.perPackPrice;
      const qty = product.selectedQuantity;
      const discount = product.sellingDiscount ?? 0;
      return sum + mrp * (1 - discount / 100) * qty;
    }, 0);

    const decimalPart = totalAmount - Math.floor(totalAmount);
    let roundedAmount: number;
    if (decimalPart >= 0.5) {
      roundedAmount = Math.ceil(totalAmount);
    } else {
      roundedAmount = Math.floor(totalAmount);
    }
    const roundOffValue = parseFloat((roundedAmount - totalAmount).toFixed(2));
    return { totalItems, totalAmount, roundedAmount, roundOffValue };
  };

  const calculateTotalDiscount = () => {
    return selectedProducts.reduce((total, product) => {
      const discount = product.sellingDiscount || 0;
      return total + ((product.perPackPrice * discount) / 100) * product.selectedQuantity;
    }, 0);
  };

  const totalMRPPrice = () => {
    return selectedProducts.reduce((total, product) => {
      return total + product.perPackPrice * product.selectedQuantity;
    }, 0);
  };

  const { totalItems, roundOffValue, roundedAmount } = calculateTotals();

  // Validate form
  const validateForm = () => {
    const errors = {
      patientName: formData?.patientName?.trim() ? "" : "Patient name is required",
      patientMobile: /^\d{10}$/.test(formData?.patientMobile) ? "" : "Valid mobile number is required",
      drName: formData?.drName?.trim() ? "" : "Doctor name is required",
      patientPincode: formData?.patientPincode?.length !== 6 ? "Valid pincode is required" : "",
    };
    return Object.values(errors).some((e) => e !== "");
  };

  const checkDoctorRequired = async (products: SelectedProduct[]) => {
    for (const product of products) {
      const check = await OrderService.checkProduct(product.product_id);
      if (check) return true;
    }
    return false;
  };

  const handlePreview = async () => {
    const isDoctorRequired = await checkDoctorRequired(selectedProducts);
    if (isDoctorRequired && validateForm()) {
      toast.error("Patient and doctor details are required for added medicines");
      return;
    }
    if (selectedProducts.length === 0) {
      toast.error("Please add at least one product");
      return;
    }
    // Dont allow submit if any product has 0 quantity
    if(selectedProducts.some(p => p.selectedQuantity === 0)) {
        toast.error("Products cannot have 0 quantity. Please update or remove them.");
        return;
    }
    if (!formData.paymentType) {
      toast.error("Payment type is required");
      return;
    }
    setShowBill(true);
  };

  const handleProcessOrder = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const isDoctorRequired = await checkDoctorRequired(selectedProducts);
      if (isDoctorRequired && validateForm()) {
        toast.error("Patient and doctor details are required for added medicines");
        return;
      }
      if (selectedProducts.length === 0) {
        toast.error("Please add at least one product");
        return;
      }
      if (selectedProducts.some(p => !p.selectedQuantity || p.selectedQuantity === 0)) {
        toast.error("Products cannot have 0 quantity. Please update or remove them.");
        return;
      }

      const totalMRPAmount = selectedProducts.reduce(
        (sum, product) => sum + product.mrp * product.selectedQuantity,
        0
      );

      const updatedProducts = selectedProducts.map((product) => {
        const itemTotalMRP = product.mrp * product.selectedQuantity;
        const contributionRatio = itemTotalMRP / totalMRPAmount;
        const distributedDiscountRs = contributionRatio * extraDiscount;
        const extraDiscountPercent = (distributedDiscountRs / itemTotalMRP) * 100;
        const finalDiscountPercent = (product.sellingDiscount || 0) + extraDiscountPercent;

        return {
          productId: product.product_id,
          noOfItems: product.selectedQuantity,
          inwardStockId: product.id,
          mrp: product.mrp,
          productDiscount: parseFloat(finalDiscountPercent.toFixed(2)),
        };
      });

      let cashAmount = 0;
      if (formData.paymentType === "Cash") {
        cashAmount = Math.max(0, roundedAmount - extraDiscount - Number(coinsused || 0));
      } else if (formData.paymentType === "Split") {
        cashAmount = formData.cashAmount;
      }

      const coinsEarned = Math.floor(
        (roundedAmount - extraDiscount - Number(coinsused || 0)) * 0.01
      );

      const payload = {
        clinicId,
        patientName: formData.patientName ?? "-",
        patientMobileNumber: formData.patientMobile ?? "-",
        patientAddress: formData.patientAddress ?? "-",
        doctorName: formData.drName ?? "-",
        patientPincode: formData.patientPincode ?? "-",
        paymentType: formData.paymentType,
        cashAmount,
        totalAmount: Math.max(0, roundedAmount - extraDiscount - Number(coinsused || 0)),
        totalItems,
        discount: extraDiscount ?? 0,
        products: updatedProducts,
        coinsEarned,
        coinsUsed: coinsused,
      };

      const response = await OfflineItemService.createOfflineOrder(payload);
      if (response.success) {
        toast.success("Order created successfully!");
        setShowBill(false);
        setSelectedProducts([]);
        handleBack();
      } else {
        throw new Error(response.message || "Failed to create order");
      }
    } catch (error) {
      console.error("Error creating offline order:", error);
      toast.error("Failed to create order");
    } finally {
      setIsProcessing(false);
    }
  };

  const calculateFinalAmount = () => {
    const discountVal = parseFloat(discountValue) || 0;
    return Math.max(0, roundedAmount - discountVal - Number(coinsused || 0));
  };

  const getDaysRemaining = (expiryDate: string) => {
    if (!expiryDate) return null;
    const today = new Date();
    const expiry = new Date(expiryDate);
    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);
    const diffTime = expiry.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };


  if (showBill) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <button
          onClick={() => setShowBill(false)}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors mb-4"
        >
          ← Back to Order
        </button>
        <div className="bg-white rounded-lg p-6 flex-1">
          <OfflineBill
            formData={formData}
            selectedProducts={selectedProducts}
            extraDiscount={extraDiscount}
            coinsUsed={coinsused}
          />
        </div>
        <div className="flex justify-end mt-4">
          <button
            onClick={handleProcessOrder}
            disabled={isProcessing}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg text-white font-semibold text-sm transition-all ${
              isProcessing
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700 shadow-md hover:shadow-lg"
            }`}
          >
            {isProcessing && <Loader2 size={16} className="animate-spin" />}
            {isProcessing ? "Processing..." : "Process Order"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-100 p-4 flex flex-col">
      <button onClick={handleBack} className="flex items-center gap-2 px-2 py-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors mb-4"><ChevronLeft/>Back</button>
      <div className="flex gap-4 items-start h-max-content">
        <div className="flex-1 flex flex-col gap-4 overflow-y-auto hiddenScrollbar">
          <PatientDetailsCard
            formData={formData}
            handleInputChange={handleInputChange}
          />

          <OrderTable
            selectedProducts={selectedProducts}
            handleQuantityInput={handleQuantityInput}
            onUpdateDiscount={onUpdateDiscount}
            handleRemoveProduct={handleRemoveProduct}
          />
        </div>

        <OrderSidebar
          totalMRPPrice={totalMRPPrice}
          calculateTotalDiscount={calculateTotalDiscount}
          roundOffValue={roundOffValue}
          existingCoins={existingCoins}
          showDiscountInput={showDiscountInput}
          setShowDiscountInput={setShowDiscountInput}
          discountValue={discountValue}
          setDiscountValue={setDiscountValue}
          setExtraDiscount={setExtraDiscount}
          formData={formData}
          handleInputChange={handleInputChange}
          calculateFinalAmount={calculateFinalAmount}
          handlePreview={handlePreview}
          setShowModal={setShowModal}
          coinsused={coinsused}
          setCoinsused={setCoinsused}
        />
      </div>
      <AddMedicineModal
        showModal={showModal}
        setShowModal={setShowModal}
        searchText={searchText}
        handleModalSearch={handleModalSearch}
        loading={loading}
        availableProducts={availableProducts}
        selectedProducts={selectedProducts}
        pagination={pagination}
        fetchProducts={fetchProducts}
        handleAddProduct={handleAddProduct}
        handleRemoveProduct={handleRemoveProduct}
        getDaysRemaining={getDaysRemaining}
      />
    </div>
  );
};

export default CreateOrder;
